import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ClipboardPlus } from 'lucide-react';

interface SimpleTaskFormProps {
  onSubmit: (taskData: {
    title: string;
    dueDate?: string;
  }) => void;
  isLoading?: boolean;
}

export const SimpleTaskForm: React.FC<SimpleTaskFormProps> = ({
  onSubmit,
  isLoading = false
}) => {
  const [title, setTitle] = useState('');
  const [dueDate] = useState(() => {
    // Set current date as default using local date components (avoids timezone issues)
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`; // Format: YYYY-MM-DD
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isLoading) return;
    
    onSubmit({
      title: title.trim() || 'Untitled Task',
      dueDate: dueDate
    });
  };

  return (
    <div className="w-full">
      {/* Description */}
      <p className="text-center text-gray-600 dark:text-gray-400 text-sm mb-6">
        Track your to-do list and stay organized with your personal tasks
      </p>
      
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Task Title */}
        <div className="space-y-2">
          <Label 
            htmlFor="task-title" 
            className="text-sm font-medium"
          >
            Task Title
          </Label>
          <div className="relative">
            <Input
              id="task-title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Enter task title"
              className="w-full px-4 py-3 rounded-xl border-2"
              disabled={isLoading}
            />
          </div>
        </div>

        {/* Due Date - Hidden field (invisibly set to current date) */}

        {/* Submit Button */}
        <div>
          <Button
            type="submit"
            disabled={isLoading}
            className="w-full bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 disabled:from-amber-300 disabled:to-yellow-300 text-white font-semibold py-4 px-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 disabled:transform-none disabled:cursor-not-allowed"
          >
            <div className="flex items-center justify-center gap-3">
              <ClipboardPlus className="w-5 h-5" />
              <span>{isLoading ? 'Creating Task...' : 'Create Task'}</span>
            </div>
          </Button>
        </div>
      </form>
    </div>
  );
}; 